import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TIER_NAMES,
  billingPortalReturned,
  startPlanRoad,
  type CheckoutOutcome,
  type PlanView,
} from "../../lib/api";
import { takePendingPortal } from "../../lib/draft";
import { planQuery, qk } from "../../lib/queries";
import { InvoicesView } from "../plan/invoices";
import { ManageRows, PaymentNotice } from "../plan/manage";
import { useSessionExpired } from "../../lib/session";
import { PlanArea } from "../plan/plan-area";
import { UpdateCardButton } from "../plan/usage-notice";
import { PlanCard } from "../plan/plan-card";
import { PlanNotices } from "../plan/plan-notices";
import { PaygBills, PaygPanel } from "../plan/payg";
import {
  PAID_SUSPENDED_LINE,
  TRIAL_CANCEL_LABEL,
  PAST_DUE_USED_LINE,
  UPDATE_CARD_FIRST,
  noticesOf,
  resetsLine,
  statedDate,
} from "../plan/plan";
import { beyondShare } from "./usage-ring";
import { LinkButton } from "../ui/button";
import { NobFace } from "./mascot";
import { Group, NavRow, SidePanel } from "./surfaces";
import { useShellUi, type BillingFocus, type BillingView } from "./state";

export const BILLING_LABEL = "Plan";

const TITLE: Record<BillingView, string> = {
  home: "Plan",
  plans: "Plans",
  invoices: "Invoices and receipts",
};

/** The index's one way on, and what the row answers with. */
export const MANAGE_TITLE = "Manage";
export const CHANGE_PLAN = "Change plan";

/**
 * What the bar measures: a share of a window, and never a capacity (ruling C2).
 * It COUNTS UP, from 0% to 100% and on past it with pay as you go (owner,
 * 2026-09-24) — the same direction as the ring and its popover, so the one
 * number reads the same wherever it is drawn.
 */
export const USED_KICKER = "Used";
/** The Free card names the PLAN, and its one action is the plan (owner, 2026-08-27). */
export const FREE_HEADLINE_KICKER = "Your plan";
export const JOIN_A_PLAN = "Join a plan";
export const OF_PERIOD = "of this month";
export const OF_TRIAL = "of your trial";
export const OVERFLOW_LINE = "This month's allowance is used. Work pauses until it resets.";
/** The wall with pay as you go on is the maximum the subscriber set. */
export const PAYG_MAX_LINE =
  "This month's pay-as-you-go maximum is reached. Work pauses until you raise it or the allowance resets.";
/** Past 100% and still working — no figure of money here; it is in the section below. */
export const PAST_LINE = "Past this month's allowance — pay as you go is on.";
export const FREE_LINE = "Free holds the door. A plan pays for the AI work.";
/** An account closure waiting on payment (ruling R-0923-5 (3)). */
export const CLOSURE_TITLE = "Closing your account";
/** The one thing this panel says about what a plan is for. */
export const PLAN_IS_THE_PRODUCT = "A plan pays for the AI work. Receiving knocks is free, always.";

/**
 * THE PLAN, in one panel with two views.
 *
 * WHY IT IS NOT A PAGE OF SETTINGS. Everything else in Settings is reached by
 * opening Settings. This is reached from four other places — the usage ring,
 * the free-plan card, a paywall, and a checkout that has just closed — and a
 * view nested two levels inside another panel could not be opened directly by
 * any of them.
 *
 * IT WAS "PLAN AND CREDITS", AND IT HAD A THIRD VIEW (ruling R-0916-1). The
 * third was the whole wallet: a headline balance, the lots and their expiry,
 * the packs, the monthly ceiling, the auto-reload enrolment and the full
 * ledger. None of those things exists, and the paid reset that later stood
 * beside the plan is deleted too (ruling R-0923-3 (1)) — so this panel is the
 * plan, and nothing else.
 *
 * ONE BALANCE, WHICH IS NOT A BALANCE: a share of a window. It used to show two
 * figures side by side with a paragraph explaining that one of them resets and
 * the other does not. There is one now, and it resets.
 *
 * NO CAPACITY, HERE EITHER (ruling C2, FR-9). What the bar measures is a SHARE
 * of a period on a scale the server keeps to itself; the number that would turn
 * that share back into a count is the one that never leaves the server. A PRICE
 * is allowed and appears on the plan card, beside the control that agrees to it.
 *
 * THE CANCEL PATH IS MEASURED FROM HERE (docs/plans.md §9 #4). Opening this
 * panel is a step, and it is a step on BOTH paths — subscribing and cancelling
 * both start by opening it, both then take exactly two more interactions, and
 * the interaction test counts them that way.
 */
export function BillingPanel({
  view,
  focus,
  paid,
}: {
  view: BillingView;
  /** Where in the view the reader was sent — the options, from "Get more usage". */
  focus?: BillingFocus | undefined;
  paid?: CheckoutOutcome;
}) {
  const ui = useShellUi();
  const sessionExpired = useSessionExpired();
  /**
   * "Cancel", pressed on the INDEX, and the confirmation lives one view over.
   *
   * There is exactly one cancel path in this product (§9 #4) and it is the
   * confirmation `PlanScreen` owns — so the index does not grow a second one,
   * it carries the intent across. The flag survives the view change because
   * this component does not unmount when `view` moves, and it is spent the
   * moment the confirmation opens so that coming back to the ladder later does
   * not reopen a question nobody asked twice.
   */
  const [cancelIntent, setCancelIntent] = useState(false);
  const queryClient = useQueryClient();

  /**
   * BACK FROM THE PROCESSOR'S BILLING PORTAL (wave 87). The round trip left a
   * record (`lib/draft.ts`); taking it asks the server to read the customer
   * fresh, so the new card shows now rather than when the webhook lands, and
   * the plan read is refreshed with it. Taken once: a second mount finds
   * nothing to take.
   */
  useEffect(() => {
    if (!takePendingPortal()) return;
    void billingPortalReturned().then(async () => {
      // A plan read already in flight was asked before the sync wrote: it is
      // cancelled rather than joined, so the read that follows is a new one.
      await queryClient.cancelQueries({ queryKey: qk.plan(), exact: true });
      await queryClient.invalidateQueries({ queryKey: qk.plan() });
    });
  }, [queryClient]);

  return (
    <SidePanel
      title={TITLE[view]}
      label={BILLING_LABEL}
      wide
      onBack={view === "home" ? null : () => ui.openBilling("home")}
      onClose={ui.closeOverlay}
    >
      {view === "home" && (
        <BillingHome
          onCancelPlan={() => {
            setCancelIntent(true);
            ui.openBilling("plans");
          }}
        />
      )}

      {view === "invoices" && <InvoicesView />}

      {view === "plans" && (
        <PlanArea
          paid={paid}
          toOptions={focus === "options"}
          toTrial={focus === "trial"}
          openCancel={cancelIntent}
          onCancelOpened={() => setCancelIntent(false)}
          onSessionExpired={sessionExpired}
        />
      )}
    </SidePanel>
  );
}

/**
 * Where you stand, in one card, and the way to change it.
 *
 * NOTHING HERE IS GUESSED AT WHILE A READ IS IN FLIGHT. A plan that has not
 * arrived draws no tier name and no bar — a zero-width bar under the word
 * "Free" is a statement about somebody's money, and it would be one we had no
 * evidence for.
 */
function BillingHome({ onCancelPlan }: { onCancelPlan: () => void }) {
  const ui = useShellUi();
  const { data: view } = useQuery({ ...planQuery(), retry: false });

  return (
    <>
      <div className="flex flex-col items-center gap-2 px-4 pt-6 pb-5">
        <NobFace mood={standing(view)} size="xl" />
        {view && (
          <>
            <p className="text-lg font-semibold tracking-tight">
              {TIER_NAMES[view.plan.tier]}
              {view.plan.state === "trialing" && " trial"}
            </p>
            <p className="text-xs text-muted">{intervalLine(view)}</p>
          </>
        )}
      </div>

      {/* AN ACCOUNT CLOSURE WAITING ON PAYMENT (ruling R-0923-5 (3)): first,
          because it is the one thing on this panel the subscriber asked for
          and is waiting on. The server's sentence, and each unpaid bill with
          the processor's page to pay it on. */}
      {view?.closure && (
        <div
          className="mx-4 mb-4 rounded-lg border border-border-strong bg-bg p-4"
          data-closure={view.closure.state}
          role="status"
        >
          <p className="text-sm font-medium text-text">{CLOSURE_TITLE}</p>
          <p className="mt-1 text-[13px] leading-snug text-text-soft">{view.closure.line}</p>
          {view.closure.bills.length > 0 && <PaygBills bills={view.closure.bills} />}
        </div>
      )}

      {/* The owner's notices (ruling R-0923-1 (5)) and pay as you go's own
          (R-0923-3 (8)), above the share they explain, and only when the
          server sent some. */}
      {view && <PlanNotices notices={noticesOf(view)} className="mx-4 mb-3" />}

      {view && (
        <div className="mx-4 mb-5 rounded-lg border border-border-soft bg-bg p-4">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.12em] text-faint">
                {view.plan.metered ? USED_KICKER : FREE_HEADLINE_KICKER}
              </p>
              {/* On Free the headline is the PLAN and there is no share to
                  draw: this tier does no AI work, so a percentage of its
                  allowance would be a percentage of nothing. On a metered
                  plan the figure COUNTS UP and is never capped (owner,
                  2026-09-24, replacing the 2026-09-23 count-down): 0% to
                  100%, then on past it, as the ring does — one direction
                  everywhere the share is drawn. */}
              <p className="text-[28px] font-semibold leading-none tabular-nums">
                {view.plan.metered ? `${usedPercent(view)}%` : TIER_NAMES.free}
              </p>
            </div>
            {view.plan.metered ? (
              <p className="shrink-0 text-right text-xs text-muted">
                {view.plan.state === "trialing" ? OF_TRIAL : OF_PERIOD}
              </p>
            ) : (
              <LinkButton
                type="button"
                className="shrink-0 text-xs"
                onClick={() => ui.openBilling("plans")}
              >
                {JOIN_A_PLAN}
              </LinkButton>
            )}
          </div>

          {/* The bar measures the PLAN's window, which is the thing that
              resets, and there is nothing else on this panel to measure. Up to
              100% it fills with what is USED; past it, the allowance and the
              part beyond split the bar in their true proportion (owner,
              2026-09-24; `beyondShare`) — the same two numbers the ring's
              popover draws. */}
          {view.plan.metered && view.plan.used_share <= 1 && (
            <div
              className="mt-3 h-1.5 overflow-hidden rounded-full bg-search"
              role="presentation"
              aria-hidden
            >
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${usedPercent(view)}%` }}
              />
            </div>
          )}
          {view.plan.metered && view.plan.used_share > 1 && (
            <div
              className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-search"
              role="presentation"
              aria-hidden
              data-past-allowance
            >
              <div
                className="h-full rounded-l-full bg-accent"
                style={{ width: `${(1 - beyondShare(view.plan.used_share)) * 100}%` }}
              />
              <div
                className="h-full rounded-r-full bg-brass"
                style={{ width: `${beyondShare(view.plan.used_share) * 100}%` }}
                data-beyond
              />
            </div>
          )}

          <p className="mt-2 text-[11px] leading-snug text-muted">{stateLine(view)}</p>
          {/* A dispute is open (ToU 2.2 §8.9). One quiet line, no action: there
              is nothing on this panel that can be bought past it. */}
          {view.plan.paid_features_suspended && (
            <p className="mt-1 text-[11px] leading-snug text-text-soft" role="status">
              {PAID_SUSPENDED_LINE}
            </p>
          )}
          {/* The same pair as the plan screen's, under the same rule: the
              sentence that says a charge is coming carries the way to stop it,
              and it DISAPPEARS once it has been stopped. A cancelled trial
              becomes a paid plan on no date at all, so the two lines below are
              exclusive rather than stacked (owner, 2026-08-26). */}
          {view.plan.state === "trialing" &&
            view.plan.trial_end &&
            !view.plan.cancel_requested_at && (
              <p className="mt-1 text-[11px] leading-snug text-muted">
                Becomes a paid plan on {statedDate(view.plan.trial_end)} unless you cancel.{" "}
                <LinkButton type="button" onClick={onCancelPlan}>
                  {TRIAL_CANCEL_LABEL}
                </LinkButton>
              </p>
            )}
          {view.plan.cancel_requested_at && (
            <p className="mt-1 text-[11px] leading-snug text-muted">
              Ending on {statedDate(view.plan.period_end)}. Nothing is deleted; you drop to Free.
            </p>
          )}

          {/* THE WAY PAST A SPENT PAID PLAN — the rung above, priced — inline,
              under the share it answers, rather than behind a row: somebody
              reading a used-up window is reading the exact question this
              answers. (The paid reset that stood here is deleted, ruling
              R-0923-3 (1).) */}
          {view.plan.state === "past_due" && (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-past-due-change>
              <p className="text-[13px] leading-snug text-text-soft">{UPDATE_CARD_FIRST}</p>
              <UpdateCardButton className="h-9 px-3" />
            </div>
          )}
          {view.plan.metered &&
            view.plan.overflow &&
            view.plan.state !== "past_due" &&
            !startPlanRoad(view.plan) &&
            view.plan.next_tier !== null && (
              <PlanCard
                nextTier={view.plan.next_tier}
                ladder={view.ladder}
                startPlan={startPlanRoad(view.plan)}
                onSeePlans={() => ui.openBilling("plans")}
                priceSuffix={view.tax.suffix}
              />
            )}
        </div>
      )}

      {/* PAY AS YOU GO, IN BILLING SETTINGS (ruling R-0923-3 (2)): on paid
          plans only — the server sends nothing on a trial or Free. Turn it
          on, change the maximum or turn it off, each a consent of its own;
          what it has come to this window; and its invoices. */}
      {view?.payg && (
        <div className="mx-4 mb-5" data-payg-settings>
          <PaygPanel payg={view.payg} mode="settings" />
        </div>
      )}

      {/* MANAGE (wave 87): a payment that needs the subscriber first, then
          the plan, the card, the billing details and the invoices — each row
          only where it has something behind it (`plan/manage.tsx`). */}
      <Group title={MANAGE_TITLE}>
        <PaymentNotice billing={view?.billing} />
        {view?.plan.state !== "past_due" && (
          <NavRow
            label={CHANGE_PLAN}
            {...(view ? { value: TIER_NAMES[view.plan.tier] } : {})}
            onClick={() => ui.openBilling("plans")}
          />
        )}
        <ManageRows billing={view?.billing} onInvoices={() => ui.openBilling("invoices")} />
      </Group>

      {/* A "Recent" group stood here, eight rows of ledger, and then the whole
          credits view it summarised (owner, 2026-08-26; ruling R-0916-1). The
          first went because a summary of a summary is not a third answer to a
          question two rows already answered; the second went with the thing it
          accounted for. What is left is a state and one door. */}

      <p className="px-6 pt-1 pb-8 text-center text-[11px] leading-snug text-fainter">
        {PLAN_IS_THE_PRODUCT}
      </p>
    </>
  );
}

/** How much of the window is used, as a whole number — never capped at 100. */
function usedPercent(view: PlanView): number {
  return Math.round(Math.max(view.plan.used_share, 0) * 100);
}

/** The mascot's own reading of the situation. Nothing else depends on it. */
function standing(view: PlanView | null | undefined) {
  if (!view) return "watching" as const;
  if (!view.plan.overflow) return "pleased" as const;
  // Out of room, and whether there is a way on decides between resignation and
  // a shrug: an offer on screen is something to be doubtful about, no offer at
  // all is just waiting for the period to turn over.
  return view.plan.next_tier ? ("watching" as const) : ("doubting" as const);
}

/** What is being paid, and how often. Never a capacity (ruling C2). */
function intervalLine(view: PlanView): string {
  if (!view.plan.metered) return "No AI work on this tier";
  return view.plan.interval === "annual" ? "Billed yearly" : "Billed monthly";
}

/**
 * The one line under the bar, and it branches on the two facts a client is
 * allowed to have: whether this tier meters work at all (ruling C10), and
 * whether the allowance is behind them (`overflow` — a lane, not a verdict).
 */
function stateLine(view: PlanView): string {
  if (!view.plan.metered) return FREE_LINE;
  // The date rides with "used" here (wave 61): the plan card under this line
  // no longer says either, so this is the one place the panel says when.
  if (view.plan.overflow && view.plan.state === "past_due") return PAST_DUE_USED_LINE;
  if (view.plan.overflow) {
    const wall = view.payg?.on ? PAYG_MAX_LINE : OVERFLOW_LINE;
    return view.plan.resets_on ? `${wall} Resets on ${statedDate(view.plan.resets_on)}.` : wall;
  }
  if (view.plan.used_share > 1) {
    return view.plan.resets_on
      ? `${PAST_LINE} Resets on ${statedDate(view.plan.resets_on)}.`
      : PAST_LINE;
  }
  return view.plan.resets_on ? resetsLine(view.plan) : "";
}
